import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import Select from 'react-select';

// Objeto com as configurações de estilo para cada tamanho
const sizeStyles = {
  sm: {
    control: base => ({
      ...base,
      minHeight: '31px',
      height: '31px',
      fontSize: '0.875rem',
      borderRadius: '0.25rem',
      width: '100%',
      minWidth: '150px' // Largura mínima para o controle
    }),
    valueContainer: base => ({
      ...base,
      padding: '0 8px',
      height: '29px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: 'calc(100% - 20px)' // Deixa espaço para o indicador
    }),
    input: base => ({
      ...base,
      margin: '0',
      paddingBottom: '0',
      paddingTop: '0'
    }),
    indicatorsContainer: base => ({
      ...base,
      height: '29px'
    }),
    indicatorSeparator: base => ({
      ...base,
      margin: '3px 0'
    }),
    dropdownIndicator: base => ({
      ...base,
      padding: '4px'
    }),
    placeholder: base => ({
      ...base,
      fontSize: '0.875rem'
    }),
    option: base => ({
      ...base,
      fontSize: '0.875rem',
      padding: '4px 12px'
    }),
    singleValue: base => ({
      ...base,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: 'calc(100% - 8px)'
    })
  },
  md: {
    control: base => ({
      ...base,
      minHeight: '38px',
      height: '38px',
      fontSize: '1rem',
      borderRadius: '0.375rem',
      width: '100%',
      minWidth: '180px' // Largura mínima para o controle
    }),
    valueContainer: base => ({
      ...base,
      padding: '0 12px',
      height: '36px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: 'calc(100% - 24px)' // Deixa espaço para o indicador
    }),
    input: base => ({
      ...base,
      margin: '0',
      paddingBottom: '0',
      paddingTop: '0'
    }),
    indicatorsContainer: base => ({
      ...base,
      height: '36px'
    }),
    indicatorSeparator: base => ({
      ...base,
      margin: '5px 0'
    }),
    dropdownIndicator: base => ({
      ...base,
      padding: '6px'
    }),
    placeholder: base => ({
      ...base,
      fontSize: '1rem'
    }),
    option: base => ({
      ...base,
      fontSize: '1rem',
      padding: '6px 16px'
    }),
    singleValue: base => ({
      ...base,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: 'calc(100% - 8px)'
    })
  },
  lg: {
    control: base => ({
      ...base,
      minHeight: '48px',
      height: '48px',
      fontSize: '1.125rem',
      borderRadius: '0.5rem',
      width: '100%',
      minWidth: '200px' // Largura mínima para o controle
    }),
    valueContainer: base => ({
      ...base,
      padding: '0 16px',
      height: '46px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: 'calc(100% - 30px)' // Deixa espaço para o indicador
    }),
    input: base => ({
      ...base,
      margin: '0',
      paddingBottom: '0',
      paddingTop: '0'
    }),
    indicatorsContainer: base => ({
      ...base,
      height: '46px'
    }),
    indicatorSeparator: base => ({
      ...base,
      margin: '8px 0'
    }),
    dropdownIndicator: base => ({
      ...base,
      padding: '8px'
    }),
    placeholder: base => ({
      ...base,
      fontSize: '1.125rem'
    }),
    option: base => ({
      ...base,
      fontSize: '1.125rem',
      padding: '8px 20px'
    }),
    singleValue: base => ({
      ...base,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: 'calc(100% - 8px)'
    })
  }
};

// Estilo base para o componente
const customStyles = {
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#0d6efd'
      : state.isFocused
      ? '#e9ecef'
      : 'transparent',
    color: state.isSelected ? 'white' : '#212529',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: state.isSelected ? '#0d6efd' : '#dee2e6'
    }
  }),
  menu: provided => ({
    ...provided,
    zIndex: 1000,
    width: 'auto', // O menu se adapta ao conteúdo
    minWidth: '100%' // Mas nunca menor que o controle
  }),
  menuList: provided => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0
  })
};

const SingleSelect = forwardRef(
  ({ options, placeholder, size = 'md', styles = {}, ...rest }, ref) => {
    // Calcula a largura máxima necessária baseada na opção mais longa
    const calculateMaxWidth = () => {
      let maxLabelLength = placeholder ? placeholder.length : 0;

      if (options && options.length > 0) {
        options.forEach(option => {
          if (option.label && option.label.length > maxLabelLength) {
            maxLabelLength = option.label.length;
          }
        });
      }

      // Adiciona um buffer para garantir espaço suficiente (ajuste conforme necessário)
      const buffer = size === 'sm' ? 2 : size === 'md' ? 3 : 4;
      return `${maxLabelLength + buffer}ch`;
    };

    // Calcula a largura necessária
    const calculatedWidth = calculateMaxWidth();

    // Mesclando os estilos base com os estilos específicos do tamanho
    // e os estilos personalizados com a largura calculada
    const mergedStyles = {
      ...customStyles,
      ...(sizeStyles[size] || sizeStyles.md), // Usa 'md' como padrão se o tamanho não existir
      control: base => ({
        ...(sizeStyles[size] || sizeStyles.md).control(base),
        minWidth: calculatedWidth
      }),
      ...styles
    };

    return (
      <Select
        ref={ref}
        closeMenuOnSelect={false}
        options={options}
        placeholder={placeholder}
        classNamePrefix="react-select"
        {...rest}
        styles={mergedStyles}
      />
    );
  }
);

SingleSelect.propTypes = {
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  styles: PropTypes.object
};

SingleSelect.defaultProps = {
  size: 'md',
  placeholder: 'Selecione...'
};

export default SingleSelect;
